import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { WINDOW } from 'src/app/@utils/window.token';

const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;

export function appScrollTrackingInitializer(): () => void {
  const mixpanelService = inject(MixpanelService);
  const destroyRef = inject(DestroyRef);
  const windowRef = inject(WINDOW);
  const documentRef = inject(DOCUMENT);
  const router = inject(Router);

  return (): void => {
    if (!windowRef) {
      return;
    }

    const trackedThresholds = new Set<number>();

    router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        tap(() => trackedThresholds.clear()),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe();

    fromEvent(windowRef, 'scroll')
      .pipe(
        debounceTime(100),
        map(() => calculateScrollDepth(windowRef, documentRef)),
        distinctUntilChanged(),
        tap((depth) => {
          trackNewlyReachedThresholds(
            depth,
            trackedThresholds,
            mixpanelService
          );
        }),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe();
  };
}

function calculateScrollDepth(
  windowRef: Window,
  documentRef: Document
): number {
  const windowHeight = windowRef.innerHeight;
  const documentHeight =
    documentRef.documentElement.scrollHeight - windowHeight;

  if (documentHeight <= 0) {
    return 0;
  }

  const scrollTop = windowRef.scrollY;
  return Math.round((scrollTop / documentHeight) * 100);
}

function trackNewlyReachedThresholds(
  depth: number,
  trackedThresholds: Set<number>,
  mixpanelService: MixpanelService
): void {
  for (const threshold of SCROLL_THRESHOLDS) {
    if (depth >= threshold && !trackedThresholds.has(threshold)) {
      trackedThresholds.add(threshold);
      mixpanelService.trackEvent('scrollDepth', { depth: threshold });
    }
  }
}
